// ================================================================================
// PlatoResultsContainer.jsx — Main orchestrating component
// KPI cards, header, performance panel, and layout grid are all kept here.
// Sub-sections are delegated to isolated containers in this folder.
// ================================================================================

import React, { useState, useEffect } from "react";
import {
    AlertTriangle, TrendingUp, TrendingDown, Scale, DollarSign,
    Users, Clock, Activity, MessageCircle, MapPin
} from "lucide-react";

// --- Sub-containers ---
import CampaignModeIndicator from "./CampaignModeIndicator";
import ResourceAllocation from "./ResourceAllocation";
import StrategicOverviewPanel from "./StrategicOverviewPanel";
import WardDeepDive from "./WardDeepDive";
import RightSidebar from "./RightSidebar";

// --- Shared atoms & helpers ---
import { GradeBadge, ModernProgressBar, WardStat } from "./SharedComponents";
import {
    formatChannelName, formatResourceValue,
    getStrategicDriver, getStrategicAlignment, getUntappedPotential
} from "./helpers";

// ================================================================================
// MAIN COMPONENT
// ================================================================================

export const ResultsDisplay = ({ results, campaignInput, openChannel, setOpenChannel, campaignId, setActiveView }) => {

    // --- Multi-ward state ---
    const [selectedWardId, setSelectedWardId] = useState(null);

    const wardKeys = Object.keys(results?.ward_level_analysis ?? {});

    useEffect(() => {
        if (wardKeys.length > 0 && !selectedWardId) {
            setSelectedWardId(wardKeys[0]);
        } else if (wardKeys.length > 0 && !wardKeys.includes(selectedWardId)) {
            setSelectedWardId(wardKeys[0]);
        }
    }, [results, wardKeys, selectedWardId]);

    // --- Derived: current ward ---
    const currentWardId = selectedWardId || (wardKeys.length > 0 ? wardKeys[0] : null);
    const wardAnalysis = currentWardId ? results?.ward_level_analysis?.[currentWardId] : null;
    const wardContext = wardAnalysis?.ward_contextual_intelligence;

    // --- Derived: Field Physics (Conditionally extract if canvassing/door metrics exist) ---
    const fieldMetrics = Object.values(wardAnalysis?.channel_performance || {})
        .map(channel => channel?.dynamic_field_metrics)
        .find(Boolean); // Grabs the first valid dynamic_field_metrics object if it exists

    // --- Derived: campaign-level data ---
    const performance = results?.performance_dashboard;
    const campaignMetrics = results?.campaign_optimization?.campaign_efficiency_metrics;
    const monteCarloProjections = results?.monte_carlo_simulation?.vote_projections;
    const monteCarloRisks = results?.monte_carlo_simulation?.risk_metrics;
    const tacticalRecs = results?.strategic_recommendations?.tactical_recommendations ?? [];
    const allRecs = results?.campaign_optimization?.recommended_channels ?? [];
    const tacticalIntelligence = results?.tactical_intelligence || results?.tacticalIntelligence;

    // Confidence warnings
    const recommendedChannelNames = allRecs.slice(0, 3).map(r => r.channel);
    const confidenceWarnings = recommendedChannelNames.map(channelName => {
        const channelData = wardAnalysis?.channel_performance?.[channelName];
        if (channelData && channelData.confidence_score < 0.7)
            return `The projection for ${formatChannelName(channelName)} is based on limited data (Conf: ${(channelData.confidence_score * 100).toFixed(0)}%). Exercise caution.`;
        return null;
    }).filter(Boolean);

    // --- Synthesis helpers ---
    const { driver, rationale: driverRationale } = getStrategicDriver(allRecs);
    const { level: alignLevel, label: alignLabel, rationale: alignRationale } = getStrategicAlignment(
        wardContext?.artemis_strategy?.tier, results?.input_summary?.campaign_focus
    );
    const { level: potentialLevel, rationale: potentialRationale, percent: potentialPercent } = getUntappedPotential(tacticalIntelligence);

    // Fatigue
    const fatigueRatios = (wardAnalysis?.ranked_channels ?? []).map(c =>
        wardAnalysis?.channel_performance?.[c.channel]?.sophisticated_research_metrics?.power_law_fatigue_ratio
    ).filter(m => m !== undefined);
    const avgFatigueRatio = fatigueRatios.length > 0 ? fatigueRatios.reduce((a, b) => a + b, 0) / fatigueRatios.length : 1.0;
    const fatiguePercent = (1 - avgFatigueRatio) * 100;

    // Report handler
    const handleCreateReport = () => {
        const reportServiceUrl = "https://your-cloud-run-report-service-url.a.run.app";
        const reportUrl = new URL(`${reportServiceUrl}/create-report`);
        reportUrl.searchParams.append("session", results?.analysis_metadata?.session_id ?? 'unknown');
        reportUrl.searchParams.append("campaign", campaignInput?.campaign_name ?? 'untitled');
        window.open(reportUrl.toString(), '_blank');
    };

    // Allocations summary (for KPIs)
    const allocations = results?.campaign_optimization?.optimal_budget_allocation?.allocations ?? [];
    const constraintCodes = [...new Set(allocations.map(a => a.rationale_code).filter(Boolean))];
    const weightedFit = allocations.length > 0 ? allocations.reduce((acc, curr) => {
        const recData = allRecs.find(r => r.channel === curr.channel);
        const fit = (recData?.average_demographic_fit ?? 0) * 100;
        return acc + (fit * (curr.allocation_percentage / 100));
    }, 0) : 0;

    // Gross votes / hybrid flags
    const projectedGrossVotes = results?.campaign_optimization?.optimal_budget_allocation?.projected_gross_votes;
    const isHybridROI = campaignMetrics?.gross_votes_per_hour > 0 && campaignMetrics?.gross_cost_per_vote > 0;
    const isGrossVotesSplit = projectedGrossVotes !== undefined && projectedGrossVotes !== null;

    // Model scores for Performance & Risk panel
    const modelScores = [
        { label: "Resource Efficiency", score: performance?.campaign_scorecard?.component_scores?.resource_efficiency ?? 0, color: "bg-gradient-to-r from-emerald-400 to-teal-500" },
        { label: "Channel Diversity", score: performance?.campaign_scorecard?.channel_diversity_score ?? 85, color: "bg-gradient-to-r from-blue-400 to-indigo-500" },
        { label: "Expected ROI Score", score: performance?.campaign_scorecard?.expected_roi_score ?? 78, color: "bg-gradient-to-r from-cyan-400 to-blue-500" },
        { label: "Predictability", score: performance?.campaign_scorecard?.predictability_score, color: "bg-gradient-to-r from-purple-400 to-fuchsia-500" },
        { label: "Budget Utilization", score: (performance?.efficiency_metrics?.budget_utilization_rate ?? 0.95) * 100, color: "bg-gradient-to-r from-amber-400 to-orange-500" }
    ];

    if (!results) {
        return <div className="p-8 text-center text-slate-400"><p>No results data available. Please run an analysis.</p></div>;
    }

    return (
        <div className="space-y-8">

            {/* ---------------------------------------------------------------- */}
            {/* HEADER                                                            */}
            {/* ---------------------------------------------------------------- */}
            <div>
                <div className="flex justify-between items-start">
                    <div>
                        <h3 className="text-2xl font-bold text-white">MATRIX Simulation Complete</h3>
                        <p className="text-slate-400">Analysis for campaign: '{campaignInput?.campaign_name ?? 'Untitled Campaign'}'</p>
                        <div className="flex space-x-4 text-sm text-slate-500 mt-1">
                            <span>Focus: <span className="font-medium text-slate-300">{(results?.input_summary?.campaign_focus ?? 'N/A').replace(/_/g, ' ')}</span></span>
                            <span>Budget: <span className="font-medium text-slate-300">£{(results?.input_summary?.budget ?? 0).toLocaleString()}</span></span>
                            {results?.campaign_optimization?.optimal_budget_allocation?.total_hours_allocated > 0 && (
                                <span>Vol. Hours: <span className="font-medium text-blue-300">{(results.campaign_optimization.optimal_budget_allocation.total_hours_allocated).toFixed(0)}</span></span>
                            )}
                            <span className="text-slate-500">|</span>
                            <span>Wards Analyzed: <span className="font-medium text-white">{wardKeys.length}</span></span>
                        </div>
                    </div>
                    <div className="text-right text-xs text-slate-500">
                        <p>PLATO v{results?.analysis_metadata?.plato_version ?? 'N/A'}</p>
                        <p>Session: {results?.analysis_metadata?.session_id ?? 'N/A'}</p>
                    </div>
                </div>
            </div>

            {/* ---------------------------------------------------------------- */}
            {/* KPI CARDS                                                        */}
            {/* ---------------------------------------------------------------- */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <GradeBadge grade={performance?.campaign_scorecard?.overall_grade ?? 'N/A'} />

                <div className="kpi-card">
                    <div className="kpi-title">Top Channel</div>
                    <div className="kpi-value text-accent-400">{formatChannelName(campaignMetrics?.best_channel)}</div>
                    <div className="text-xs text-slate-400 mt-1">
                        {campaignMetrics?.gross_votes_per_hour > 0 ? "Highest Multi-Resource Efficiency" : "Highest Financial ROI"}
                    </div>
                </div>

                {/* Projected Votes */}
                <div className={`kpi-card ${isGrossVotesSplit ? 'p-0 overflow-hidden flex flex-col justify-between' : ''}`}>
                    {isGrossVotesSplit ? (
                        <>
                            <div className="flex-1 px-5 py-2.5 border-b border-slate-700/50 flex flex-col justify-center bg-slate-800/20">
                                <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center justify-between">
                                    <span>Total Outcome (Gross)</span>
                                    <Users size={10} />
                                </div>
                                <div className="text-xl font-bold text-white font-mono">{Math.round(projectedGrossVotes ?? 0).toLocaleString()} Votes</div>
                            </div>
                            <div className="flex-1 px-5 py-2.5 bg-slate-800/40 flex flex-col justify-center">
                                <div className="text-[10px] uppercase tracking-wider text-accent-300 flex items-center justify-between">
                                    <span>Projected Lift</span>
                                    <TrendingUp size={10} />
                                </div>
                                <div className="text-xl font-bold text-accent-400 font-mono">
                                    +{Math.round(results?.campaign_optimization?.optimal_budget_allocation?.expected_total_votes ?? 0).toLocaleString()} Votes
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="kpi-title">Projected Votes (Mean)</div>
                            <div className="kpi-value">{(monteCarloProjections?.mean ?? 0).toFixed(0)}</div>
                            <div className="text-xs text-slate-400 mt-1">Range: {(monteCarloProjections?.['5th_percentile'] ?? 0).toFixed(0)} - {(monteCarloProjections?.['95th_percentile'] ?? 0).toFixed(0)} (95% CI)</div>
                        </>
                    )}
                </div>

                {/* ROI */}
                <div className={`kpi-card ${isHybridROI ? 'p-0 overflow-hidden flex flex-col justify-between' : ''}`}>
                    {isHybridROI ? (
                        <>
                            <div className="flex-1 px-5 py-2.5 border-b border-slate-700/50 flex flex-col justify-center bg-slate-800/20">
                                <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center justify-between">
                                    <span>Financial</span>
                                    <DollarSign size={10} />
                                </div>
                                <div className="text-xl font-bold text-white font-mono">£{(campaignMetrics?.gross_cost_per_vote ?? 0).toFixed(2)} / Vote</div>
                            </div>
                            <div className="flex-1 px-5 py-2.5 bg-slate-800/40 flex flex-col justify-center">
                                <div className="text-[10px] uppercase tracking-wider text-blue-300 flex items-center justify-between">
                                    <span>Time</span>
                                    <Clock size={10} />
                                </div>
                                <div className="text-xl font-bold text-blue-400 font-mono">{(campaignMetrics?.gross_votes_per_hour ?? 0).toFixed(1)} Votes / Hr</div>
                            </div>
                        </>
                    ) : (
                        <>
                            {campaignMetrics?.gross_votes_per_hour > 0 && campaignMetrics?.gross_cost_per_vote === 0 ? (
                                <>
                                    <div className="kpi-title">Votes Per Hour</div>
                                    <div className="kpi-value text-blue-400">{(campaignMetrics.gross_votes_per_hour).toFixed(1)}</div>
                                    <div className="text-xs text-slate-400 mt-1">Volunteer Efficiency</div>
                                </>
                            ) : (
                                <>
                                    <div className="kpi-title">Eff. Cost / Vote</div>
                                    <div className="kpi-value">£{(campaignMetrics?.gross_cost_per_vote ?? 0).toFixed(2)}</div>
                                    <div className="text-xs text-slate-400 mt-1">Financial Efficiency</div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* ---------------------------------------------------------------- */}
            {/* DYNAMIC FIELD METRICS (Conditionally Rendered)                     */}
            {/* ---------------------------------------------------------------- */}
            {fieldMetrics && (
                <div className="bg-slate-800/20 border border-slate-700/50 rounded-xl p-5">
                    <h5 className="text-xs font-bold text-slate-400 mb-4 uppercase tracking-wider">Field Operations Physics</h5>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-3 flex flex-col justify-center">
                            <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-1">
                                <MapPin size={12} className="text-emerald-400" /> Transit Time
                            </div>
                            <div className="text-lg font-bold text-white font-mono">
                                {fieldMetrics.transit_time.toFixed(1)} <span className="text-xs text-slate-500 font-sans">mins</span>
                            </div>
                        </div>
                        <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-3 flex flex-col justify-center">
                            <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-1">
                                <Activity size={12} className="text-blue-400" /> Answer Rate
                            </div>
                            <div className="text-lg font-bold text-white font-mono">
                                {(fieldMetrics.answer_rate * 100).toFixed(1)}<span className="text-xs text-slate-500 font-sans">%</span>
                            </div>
                        </div>
                        <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-3 flex flex-col justify-center">
                            <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-1">
                                <MessageCircle size={12} className="text-purple-400" /> Conversation
                            </div>
                            <div className="text-lg font-bold text-white font-mono">
                                {fieldMetrics.conversation_time.toFixed(1)} <span className="text-xs text-slate-500 font-sans">mins</span>
                            </div>
                        </div>
                        <div className="bg-accent-500/10 border border-accent-500/30 rounded-lg p-3 flex flex-col justify-center shadow-[inset_0_0_15px_rgba(0,0,0,0.2)]">
                            <div className="text-[10px] uppercase tracking-wider text-accent-300 flex items-center gap-1.5 mb-1">
                                <Clock size={12} /> Effective Door Time
                            </div>
                            <div className="text-lg font-bold text-accent-400 font-mono">
                                {fieldMetrics.effective_time.toFixed(2)} <span className="text-xs text-accent-500/70 font-sans">mins/door</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------------------------------------------------------------- */}
            {/* PERFORMANCE & RISK PANEL                                         */}
            {/* ---------------------------------------------------------------- */}
            <div className="panel p-6">
                <h5 className="text-base font-semibold text-white mb-4">Performance & Risk</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-4">
                    {modelScores.map(item => (
                        <ModernProgressBar key={item.label} label={item.label} score={item.score} colorClass={item.color || "bg-accent-500"} />
                    ))}
                </div>
                <div className="border-t border-slate-700 mt-6 pt-4">
                    <h6 className="text-sm font-semibold text-white mb-3">Risk Analysis</h6>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <WardStat icon={<AlertTriangle size={20} className="text-yellow-400" />} label="Volatility Index" value={`${((monteCarloRisks?.volatility_index ?? 0) * 100).toFixed(1)}%`} />
                        <WardStat icon={<TrendingUp size={20} className="text-success-400" />} label="Upside Potential (95th %)" value={`${(monteCarloProjections?.['95th_percentile'] ?? 0).toFixed(0)} votes`} />
                        <WardStat icon={<TrendingDown size={20} className="text-danger-400" />} label="Downside Risk (5th %)" value={`${(monteCarloProjections?.['5th_percentile'] ?? 0).toFixed(0)} votes`} />
                    </div>
                </div>
                {constraintCodes.length > 0 && (
                    <div className="border-t border-slate-700 mt-6 pt-4">
                        <h6 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                            <Scale size={16} className="text-slate-400" /> Constraint Logic Analysis
                        </h6>
                        <p className="text-xs text-slate-400">
                            Primary optimization constraints: <span className="text-slate-200">{constraintCodes.map(code => code.replace(/_/g, ' ')).join(', ')}</span>
                        </p>
                    </div>
                )}
            </div>

            {/* ---------------------------------------------------------------- */}
            {/* PARENT GRID: Left/Main Column + Right Sidebar                    */}
            {/* ---------------------------------------------------------------- */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* LEFT / MAIN COLUMN */}
                <div className="lg:col-span-2 space-y-8">

                    {/* Campaign Mode Indicator */}
                    <CampaignModeIndicator results={results} campaignInput={campaignInput} />

                    {/* Resource Allocation */}
                    <ResourceAllocation
                        results={results}
                        allRecs={allRecs}
                        campaignMetrics={campaignMetrics}
                        weightedFit={weightedFit}
                    />

                    {/* Strategic Overview (Surplus / Canvassing / Resonance) */}
                    <StrategicOverviewPanel results={results} currentWardId={currentWardId} />

                    {/* Ward Deep Dive */}
                    <WardDeepDive
                        results={results}
                        wardAnalysis={wardAnalysis}
                        wardContext={wardContext}
                        currentWardId={currentWardId}
                        wardKeys={wardKeys}
                        setSelectedWardId={setSelectedWardId}
                        allRecs={allRecs}
                        openChannel={openChannel}
                        setOpenChannel={setOpenChannel}
                        alignLevel={alignLevel}
                        alignLabel={alignLabel}
                        alignRationale={alignRationale}
                        fatiguePercent={fatiguePercent}
                        tacticalRecs={tacticalRecs}
                    />

                </div>

                {/* RIGHT SIDEBAR */}
                <RightSidebar
                    results={results}
                    wardAnalysis={wardAnalysis}
                    wardContext={wardContext}
                    currentWardId={currentWardId}
                    monteCarloRisks={monteCarloRisks}
                    monteCarloProjections={monteCarloProjections}
                    tacticalRecs={tacticalRecs}
                    confidenceWarnings={confidenceWarnings}
                    potentialLevel={potentialLevel}
                    potentialPercent={potentialPercent}
                    potentialRationale={potentialRationale}
                    driver={driver}
                    driverRationale={driverRationale}
                    campaignId={campaignId}
                    setActiveView={setActiveView}
                    handleCreateReport={handleCreateReport}
                />

            </div>

            {/* LEGAL DISCLAIMER */}
            <div className="text-center text-xs text-white-600 mt-9 pb-5">
                Dante is for informational purposes only. <br />Outputs are probabilistic estimates, not guarantees. Independent verification required prior to capital allocation.
            </div>
        </div>
    );
};